import type { ResumeDocument } from "@/types/cv";
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  LineRuleType,
  type ParagraphChild,
  Paragraph,
  Tab,
  TabStopType,
  TextRun,
} from "docx";
import { normalizeRewriteResumeSections } from "../rewrite-section-normalizer";
import { formatDateRange, formatMonthYear } from "./utils";

type HeadingTransform = "uppercase" | "title" | "none";

type AlignmentValue = (typeof AlignmentType)[keyof typeof AlignmentType];

export type ResumeDocxTemplateOptions = {
  accentColor: string;
  headingFont: string;
  bodyFont: string;
  headerAlignment?: AlignmentValue;
  headingTransform?: HeadingTransform;
  headingUnderline?: boolean;
  summaryAlignment?: AlignmentValue;
  nameFontSize?: number;
  includeOrganizations?: boolean;
  includeHighlights?: boolean;
  contactSeparator?: string;
  bulletSpacing?: number;
};

type ResolvedDocxTemplateOptions = Required<
  Omit<
    ResumeDocxTemplateOptions,
    | "headingTransform"
    | "headerAlignment"
    | "summaryAlignment"
    | "contactSeparator"
    | "nameFontSize"
  >
> & {
  headingTransform: HeadingTransform;
  headerAlignment: AlignmentValue;
  summaryAlignment: AlignmentValue;
  contactSeparator: string;
  nameFontSize: number;
  bulletSpacing: number;
  accentColorHex: string;
  mutedColor: string;
  pageWidthTwips: number;
  pageHeightTwips: number;
  pageMarginsTwips: { top: number; bottom: number; left: number; right: number };
  rightTabStopTwips: number;
};

const defaultOptions = {
  headingTransform: "uppercase" as HeadingTransform,
  headerAlignment: AlignmentType.CENTER,
  summaryAlignment: AlignmentType.JUSTIFIED,
  headingUnderline: false,
  includeOrganizations: true,
  includeHighlights: true,
  contactSeparator: " · ",
  nameFontSize: 26,
  bulletSpacing: 80,
  mutedColor: "6B7280",
};

const PAGE_WIDTH_TWIPS = 12240;
const PAGE_HEIGHT_TWIPS = 15840;
const PAGE_MARGINS_TWIPS = {
  top: 720,
  bottom: 720,
  left: 720,
  right: 720,
} as const;

const INLINE_SEPARATOR = " · ";

const sanitizeColor = (value: string) =>
  value
    .replace(/#/g, "")
    .replace(/[^0-9a-f]/gi, "")
    .toUpperCase() || "111111";

const fontSize = (points: number) => Math.max(12, Math.round(points * 2));

const resolveOptions = (
  options: ResumeDocxTemplateOptions,
): ResolvedDocxTemplateOptions => {
  const rightTabStopTwips =
    PAGE_WIDTH_TWIPS - PAGE_MARGINS_TWIPS.left - PAGE_MARGINS_TWIPS.right;
  return {
    ...defaultOptions,
    ...options,
    accentColorHex: sanitizeColor(options.accentColor),
    headingTransform:
      options.headingTransform ?? defaultOptions.headingTransform,
    headerAlignment: options.headerAlignment ?? defaultOptions.headerAlignment,
    summaryAlignment:
      options.summaryAlignment ?? defaultOptions.summaryAlignment,
    contactSeparator:
      options.contactSeparator ?? defaultOptions.contactSeparator,
    nameFontSize: options.nameFontSize ?? defaultOptions.nameFontSize,
    bulletSpacing: options.bulletSpacing ?? defaultOptions.bulletSpacing,
    pageWidthTwips: PAGE_WIDTH_TWIPS,
    pageHeightTwips: PAGE_HEIGHT_TWIPS,
    pageMarginsTwips: PAGE_MARGINS_TWIPS,
    rightTabStopTwips,
  };
};

const createSectionHeading = (
  title: string,
  theme: ResolvedDocxTemplateOptions,
) => {
  const nextTitle =
    theme.headingTransform === "uppercase"
      ? title.toUpperCase()
      : theme.headingTransform === "title"
        ? title
        : title;

  return new Paragraph({
    spacing: { before: 240, after: 80 },
    children: [
      new TextRun({
        text: nextTitle,
        bold: true,
        font: theme.headingFont,
        size: fontSize(12),
        color: theme.accentColorHex,
      }),
    ],
    border: theme.headingUnderline
      ? {
          bottom: {
            color: theme.accentColorHex,
            space: 2,
            style: BorderStyle.SINGLE,
            size: 6,
          },
        }
      : undefined,
  });
};

const createMultilineParagraph = (
  content: string,
  options?: { italics?: boolean; color?: string; alignment?: AlignmentValue },
): Paragraph | null => {
  const lines = content
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (lines.length === 0) return null;

  return new Paragraph({
    alignment: options?.alignment,
    spacing: { after: 100 },
    children: lines.map(
      (line, index) =>
        new TextRun({
          text: line,
          break: index === 0 ? undefined : 1,
          italics: options?.italics,
          color: options?.color,
        }),
    ),
  });
};

const createBulletParagraphs = (
  points: string[],
  theme: ResolvedDocxTemplateOptions,
) => {
  return points
    .map((point) => point.trim())
    .filter((point) => point.length > 0)
    .map(
      (point) =>
        new Paragraph({
          text: point,
          bullet: { level: 0 },
          spacing: { after: theme.bulletSpacing },
          indent: { left: 360, hanging: 360 },
          alignment: AlignmentType.JUSTIFIED,
        }),
    );
};

const createLeftRightParagraph = (args: {
  leftRuns: ParagraphChild[];
  rightText?: string;
  theme: ResolvedDocxTemplateOptions;
  spacingBefore?: number;
  spacingAfter?: number;
  rightColor?: string;
}) => {
  const children: ParagraphChild[] = [...args.leftRuns];
  const rightText = args.rightText?.trim();
  if (rightText) {
    children.push(new TextRun({ text: " " }));
    children.push(new TextRun({ children: [new Tab()] }));
    children.push(
      new TextRun({
        text: rightText,
        color: args.rightColor ?? args.theme.mutedColor,
      }),
    );
  }

  return new Paragraph({
    tabStops: [
      {
        type: TabStopType.RIGHT,
        position: args.theme.rightTabStopTwips,
      },
    ],
    spacing: {
      before: args.spacingBefore,
      after: args.spacingAfter,
    },
    children,
  });
};

const buildHeader = (
  resume: ResumeDocument,
  theme: ResolvedDocxTemplateOptions,
) => {
  const paragraphs: Paragraph[] = [];
  const name = resume.personal_identity.name.trim();
  if (name) {
    paragraphs.push(
      new Paragraph({
        heading: HeadingLevel.TITLE,
        alignment: theme.headerAlignment,
        spacing: { after: 60 },
        children: [
          new TextRun({
            text: name,
            bold: true,
            font: theme.headingFont,
            size: fontSize(theme.nameFontSize),
            color: theme.accentColorHex,
          }),
        ],
      }),
    );
  }

  const location = [
    resume.personal_identity.location.city.trim(),
    resume.personal_identity.location.country.trim(),
  ]
    .filter((entry) => entry && entry.length > 0)
    .join(", ");
  const contactSegments = [
    location,
    resume.personal_identity.email.trim(),
    resume.personal_identity.phone_number.trim(),
    resume.personal_identity.linkedin.trim(),
  ].filter((entry) => entry && entry.length > 0);

  if (contactSegments.length) {
    paragraphs.push(
      new Paragraph({
        alignment: theme.headerAlignment,
        spacing: { after: 40 },
        children: [
          new TextRun({
            text: contactSegments.join(theme.contactSeparator),
            color: theme.mutedColor,
          }),
        ],
      }),
    );
  }

  const additionalLinks = resume.personal_identity.additional_links
    .map((link) => {
      const label = link.label.trim();
      const url = link.url.trim();
      if (label && url) {
        return `${label}: ${url}`;
      }
      return url || label || "";
    })
    .filter((value) => value.length > 0);

  if (additionalLinks.length) {
    paragraphs.push(
      new Paragraph({
        alignment: theme.headerAlignment,
        spacing: { after: 60 },
        children: [
          new TextRun({
            text: additionalLinks.join(theme.contactSeparator),
            color: theme.mutedColor,
          }),
        ],
      }),
    );
  }

  return paragraphs;
};

const buildSummary = (
  resume: ResumeDocument,
  theme: ResolvedDocxTemplateOptions,
) => {
  const summary = resume.professional_summary.trim();
  if (!summary) return [];

  const paragraphs: Paragraph[] = [];
  paragraphs.push(createSectionHeading("Summary", theme));
  const summaryParagraph = createMultilineParagraph(summary, {
    color: "1F2933",
    alignment: theme.summaryAlignment,
  });
  if (summaryParagraph) {
    paragraphs.push(summaryParagraph);
  }
  return paragraphs;
};

type TimelineSectionEntry = {
  title: string;
  originalTitle?: string;
  entityName: string;
  metaParts?: string[];
  startDate: string | null;
  endDate: string | null;
  isCurrent?: boolean;
  detail?: string;
  points?: string[];
};

const resolveDisplayedTitle = (title: string, rewriteTitle?: string) => {
  const originalTitle = title.trim();
  const rewrittenTitle = rewriteTitle?.trim();
  const displayTitle =
    rewrittenTitle && rewrittenTitle.length > 0
      ? rewrittenTitle
      : originalTitle;

  return {
    displayTitle,
    originalTitle:
      rewrittenTitle && rewrittenTitle !== originalTitle
        ? originalTitle
        : undefined,
  };
};

const createTimelineSection = (args: {
  sectionTitle: string;
  entries: TimelineSectionEntry[];
  theme: ResolvedDocxTemplateOptions;
}) => {
  if (args.entries.length === 0) return [];

  const paragraphs: Paragraph[] = [
    createSectionHeading(args.sectionTitle, args.theme),
  ];

  args.entries.forEach((entry) => {
    const displayTitle = entry.title.trim();
    const period = formatDateRange(
      entry.startDate,
      entry.isCurrent ? null : entry.endDate,
      {
        canBeCurrent: Boolean(entry.isCurrent),
      },
    );

    if (displayTitle) {
      paragraphs.push(
        createLeftRightParagraph({
          leftRuns: [
            new TextRun({
              text: displayTitle,
              bold: true,
              font: args.theme.headingFont,
              color: args.theme.accentColorHex,
            }),
          ],
          rightText: period,
          theme: args.theme,
          spacingBefore: 140,
          spacingAfter: 20,
        }),
      );
    }

    if (entry.originalTitle && entry.originalTitle.trim()) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 20 },
          children: [
            new TextRun({
              text: `Original: ${entry.originalTitle.trim()}`,
              italics: true,
              color: args.theme.mutedColor,
            }),
          ],
        }),
      );
    }

    const entityName = entry.entityName.trim();
    const metaParts = (entry.metaParts ?? [])
      .map((part) => part.trim())
      .filter(Boolean);

    if (entityName || metaParts.length > 0) {
      const children: TextRun[] = [];
      if (entityName) {
        children.push(new TextRun({ text: entityName, bold: true }));
      }
      if (metaParts.length > 0) {
        const metaText = metaParts.join(INLINE_SEPARATOR);
        children.push(
          new TextRun({
            text: `${entityName ? INLINE_SEPARATOR : ""}${metaText}`,
            color: args.theme.mutedColor,
          }),
        );
      }

      paragraphs.push(
        new Paragraph({
          spacing: { after: 20 },
          children,
        }),
      );
    }

    const points = entry.points ?? [];
    const hasPoints = points.some((point) => point.trim().length > 0);
    const detailParagraph =
      !hasPoints && entry.detail
        ? createMultilineParagraph(entry.detail, { color: "1F2933" })
        : null;

    if (detailParagraph) {
      paragraphs.push(detailParagraph);
    }

    if (hasPoints) {
      paragraphs.push(...createBulletParagraphs(points, args.theme));
    }
  });

  return paragraphs;
};

const buildExperienceSection = (
  resume: ResumeDocument,
  theme: ResolvedDocxTemplateOptions,
) => {
  return createTimelineSection({
    sectionTitle: "Experience",
    theme,
    entries: resume.working_experience.map((experience) => {
      const { displayTitle, originalTitle } = resolveDisplayedTitle(
        experience.title,
        experience.rewrite_title,
      );

      return {
        title: displayTitle,
        originalTitle,
        entityName: experience.company,
        metaParts: [experience.location, experience.employment_type],
        startDate: experience.start_date,
        endDate: experience.end_date,
        isCurrent: experience.is_current,
        detail: experience.experience_detail,
        points: experience.points,
      };
    }),
  });
};

const buildOrganizationsSection = (
  resume: ResumeDocument,
  theme: ResolvedDocxTemplateOptions,
) => {
  if (!theme.includeOrganizations) {
    return [];
  }

  return createTimelineSection({
    sectionTitle: "Organizations",
    theme,
    entries: resume.organizations.map((organization) => {
      const { displayTitle, originalTitle } = resolveDisplayedTitle(
        organization.title,
        organization.rewrite_title,
      );

      return {
        title: displayTitle,
        originalTitle,
        entityName: organization.org,
        metaParts: [organization.location],
        startDate: organization.start_date,
        endDate: organization.end_date,
        isCurrent: organization.is_current,
        detail: organization.experience_detail,
        points: organization.points,
      };
    }),
  });
};

const buildEducationSection = (
  resume: ResumeDocument,
  theme: ResolvedDocxTemplateOptions,
) => {
  if (!resume.education.length) return [];
  const paragraphs: Paragraph[] = [createSectionHeading("Education", theme)];

  resume.education.forEach((education) => {
    const period = formatDateRange(education.start_date, education.end_date);
    const institution = education.institution.trim();
    if (institution) {
      paragraphs.push(
        createLeftRightParagraph({
          leftRuns: [
            new TextRun({
              text: institution,
              bold: true,
              font: theme.headingFont,
            }),
          ],
          rightText: period,
          theme,
          spacingBefore: 140,
          spacingAfter: 20,
        }),
      );
    }

    const rewrittenProgram = education.rewrite_program_title.trim();
    const originalProgram = education.program_title.trim();
    const programTitle =
      rewrittenProgram && rewrittenProgram.length > 0
        ? rewrittenProgram
        : originalProgram;
    if (programTitle) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 20 },
          children: [
            new TextRun({ text: programTitle, color: theme.accentColorHex }),
          ],
        }),
      );
    }

    if (rewrittenProgram && rewrittenProgram !== originalProgram) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 30 },
          children: [
            new TextRun({
              text: `Original: ${originalProgram}`,
              italics: true,
              color: theme.mutedColor,
            }),
          ],
        }),
      );
    }

    const academicDetails = [education.degree.trim(), education.major.trim()]
      .filter((entry) => entry.length > 0)
      .join(INLINE_SEPARATOR);
    const withGpa = education.gpa.trim()
      ? `${academicDetails}${academicDetails ? INLINE_SEPARATOR : ""}GPA ${education.gpa.trim()}`
      : academicDetails;
    if (withGpa) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 20 },
          children: [new TextRun({ text: withGpa, color: theme.mutedColor })],
        }),
      );
    }

    const hasPoints = education.points.some((point) => point.trim().length > 0);
    if (hasPoints) {
      paragraphs.push(...createBulletParagraphs(education.points, theme));
    }
  });

  return paragraphs;
};

const buildSkillsSection = (
  resume: ResumeDocument,
  theme: ResolvedDocxTemplateOptions,
) => {
  if (!theme.includeHighlights) return [];
  const additional = resume.additional_information;

  const joinList = (values: string[]) =>
    values
      .map((value) => value.trim())
      .filter(Boolean)
      .join(", ");

  const bulletItems: string[] = [];
  const technicalSkills = joinList(additional.technical_skills);
  if (technicalSkills) {
    bulletItems.push(`Technical Skills: ${technicalSkills}`);
  }

  const tools = joinList(additional.tools);
  if (tools) {
    bulletItems.push(`Tools: ${tools}`);
  }

  const softSkills = joinList(additional.soft_skills);
  if (softSkills) {
    bulletItems.push(`Soft Skills: ${softSkills}`);
  }

  const languages = additional.languages
    .map((language) => {
      const name = language.name.trim();
      if (!name) return "";
      const proficiency = language.proficiency.trim();
      return proficiency ? `${name} (${proficiency})` : name;
    })
    .filter(Boolean)
    .join(", ");
  if (languages) {
    bulletItems.push(`Languages: ${languages}`);
  }

  additional.certificates_awards.forEach((certificate) => {
    const rewrittenTitle = certificate.rewrite_title.trim();
    const originalTitle = certificate.title.trim();
    const displayTitle =
      rewrittenTitle && rewrittenTitle.length > 0
        ? rewrittenTitle
        : originalTitle;
    if (!displayTitle) return;

    const certificateDate = formatMonthYear(certificate.date);
    const certificateMetaParts = [certificate.issuer.trim(), certificateDate]
      .filter(Boolean)
      .join(INLINE_SEPARATOR);
    bulletItems.push(
      certificateMetaParts
        ? `${displayTitle}: ${certificateMetaParts}`
        : displayTitle,
    );
  });

  if (bulletItems.length === 0) return [];

  return [
    createSectionHeading("Skills", theme),
    ...createBulletParagraphs(bulletItems, theme),
  ];
};

export const createDocxResumeDocument = (
  resume: ResumeDocument,
  options: ResumeDocxTemplateOptions,
) => {
  const theme = resolveOptions(options);
  const normalizedResume = normalizeRewriteResumeSections(resume) ?? resume;
  const content: Paragraph[] = [];

  content.push(...buildHeader(normalizedResume, theme));
  content.push(...buildSummary(normalizedResume, theme));
  content.push(...buildExperienceSection(normalizedResume, theme));
  content.push(...buildOrganizationsSection(normalizedResume, theme));
  content.push(...buildEducationSection(normalizedResume, theme));
  content.push(...buildSkillsSection(normalizedResume, theme));

  if (!content.length) {
    content.push(new Paragraph({ text: "Resume content is not available." }));
  }

  return new Document({
    creator: "Hyred",
    description: "Hyred rewritten resume",
    sections: [
      {
        properties: {
          page: {
            margin: theme.pageMarginsTwips,
            size: {
              width: theme.pageWidthTwips,
              height: theme.pageHeightTwips,
            },
          },
        },
        children: content,
      },
    ],
    styles: {
      default: {
        document: {
          run: {
            font: theme.bodyFont,
            size: fontSize(10.5),
            color: "1F2933",
          },
          paragraph: {
            spacing: {
              line: 276,
              lineRule: LineRuleType.AUTO,
            },
          },
        },
      },
    },
  });
};
